// Lab value parsing and flags (SPEC.md §4.3 Labs). parseNumericLabValue, classifyPanelTestValue and
// qualitativeStatus come from the SaaS lib/labs.ts, reshaped to the personal lab tables. A flag only
// compares a value against the range the user can see and edit; it is not a diagnosis.
import type { Tone } from '../components/StatusBadge';

export type LabStatus = 'normal' | 'low' | 'high' | 'critical_low' | 'critical_high' | 'positive' | 'negative' | 'borderline' | 'unknown';

export interface LabRange {
  normal_min: number | null;
  normal_max: number | null;
  critical_low: number | null;
  critical_high: number | null;
}

export const STATUS_LABELS: Record<LabStatus, string> = {
  normal: 'In range',
  low: 'Low',
  high: 'High',
  critical_low: 'Very low',
  critical_high: 'Very high',
  positive: 'Reactive',
  negative: 'Non-reactive',
  borderline: 'Borderline',
  unknown: 'No range'
};

export function parseNumericLabValue(raw: string) {
  const cleaned = raw.replace(/,/g, '').trim();
  if (!cleaned) return null;
  const match = cleaned.match(/[-+]?\d*\.?\d+/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

export function classifyPanelTestValue(value: number | null, range: LabRange): LabStatus {
  if (value == null) return 'unknown';
  if (range.critical_low != null && value <= range.critical_low) return 'critical_low';
  if (range.critical_high != null && value >= range.critical_high) return 'critical_high';
  if (range.normal_min != null && value < range.normal_min) return 'low';
  if (range.normal_max != null && value > range.normal_max) return 'high';
  if (range.normal_min == null && range.normal_max == null) return 'unknown';
  return 'normal';
}

export function qualitativeStatus(raw: string): LabStatus {
  const normalized = raw.trim().toLowerCase();
  if (!normalized) return 'unknown';
  if (normalized.includes('border')) return 'borderline';
  // "non-reactive" contains "reactive", so negatives are checked first.
  if (normalized.includes('non-reactive') || normalized.includes('nonreactive') || normalized.includes('negative') || normalized === 'not detected') return 'negative';
  if (normalized.includes('reactive') || normalized.includes('positive') || normalized === 'detected') return 'positive';
  return 'unknown';
}

export function statusTone(status: LabStatus): Tone {
  if (status === 'critical_low' || status === 'critical_high') return 'danger';
  if (status === 'low' || status === 'high' || status === 'positive' || status === 'borderline') return 'warn';
  if (status === 'normal' || status === 'negative') return 'good';
  return 'neutral';
}

export const isCritical = (status: string | null) => status === 'critical_low' || status === 'critical_high';
export const isFlagged = (status: string | null) => status != null && status !== 'normal' && status !== 'negative' && status !== 'unknown';

/** "3.5–5.3", "< 69", "> 60", or null when the test has no range. */
export function describeRange(range: Pick<LabRange, 'normal_min' | 'normal_max'>) {
  const { normal_min: min, normal_max: max } = range;
  if (min != null && max != null) return `${formatNumber(min)}–${formatNumber(max)}`;
  if (max != null) return `< ${formatNumber(max)}`;
  if (min != null) return `> ${formatNumber(min)}`;
  return null;
}

/** Up to 2 decimals, with thousands separators for big counts (e.g. platelets). */
export function formatNumber(value: number) {
  return value.toLocaleString('en', { maximumFractionDigits: 2 });
}

/** Urea reduction ratio, % = (pre − post) / pre × 100 (SPEC.md §4.3 computed values). */
export function urr(preUrea: number | null | undefined, postUrea: number | null | undefined): number | null {
  if (preUrea == null || postUrea == null || preUrea <= 0 || postUrea < 0 || postUrea > preUrea) return null;
  return Math.round(((preUrea - postUrea) / preUrea) * 1000) / 10;
}
